import React, { useEffect, useState } from 'react';
import {
  Badge, Button, DataTable, IdCell, KV, Modal, PageHead, SearchBox, Textarea, dateTimeStr, num,
} from '../components/ui';
import useList from '../hooks/useList';
import { ticketApi } from '../api';
import { mediaUrl } from '../api/media';
import { useToast } from '../context/ToastContext';

const TABS = [
  { value: '', label: 'All' },
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'resolved', label: 'Resolved' },
];

const TONE = { open: 'warn', in_progress: 'info', resolved: 'success' };
const LABEL = { open: 'Open', in_progress: 'In progress', resolved: 'Resolved' };

/**
 * The support desk: every request raised from the app.
 *
 * Only this console can mark a ticket resolved; the person who raised it can
 * always reply again, which reopens it.
 */
const Tickets = () => {
  const toast = useToast();
  const list = useList(ticketApi.list, { status: '', search: '' });

  const [open, setOpen] = useState(null);
  const [reply, setReply] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  /* Re-read the thread whenever one is opened, so replies are never stale. */
  useEffect(() => {
    if (!open?._id) return;
    ticketApi
      .get(open._id)
      .then(res => setOpen(res.data))
      .catch(err => toast.error(err.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open?._id]);

  const sendReply = async () => {
    if (!reply.trim()) return toast.error('Type a reply first');
    setBusy(true);
    try {
      const res = await ticketApi.reply(open._id, reply.trim());
      setOpen(res.data);
      setReply('');
      toast.success('Reply sent');
      list.reload();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
    return undefined;
  };

  const resolve = async () => {
    setBusy(true);
    try {
      const res = await ticketApi.resolve(open._id, note);
      toast.success(res.message);
      setOpen(res.data);
      setNote('');
      list.reload();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const columns = [
    {
      key: 'ticket',
      label: 'Request',
      render: row => (
        <div>
          <div style={{ fontWeight: 600 }}>{row.title}</div>
          <div className="text-sm text-muted">{row.ticketNumber}</div>
        </div>
      ),
    },
    {
      key: 'raisedBy',
      label: 'Raised by',
      render: row => <IdCell name={row.raisedBy?.name} sub={row.company?.name || row.raisedBy?.mobile} />,
    },
    {
      key: 'replies',
      label: 'Replies',
      align: 'center',
      render: row => num(row.replyCount || 0),
    },
    {
      key: 'status',
      label: 'Status',
      render: row => <Badge status={row.status} tone={TONE[row.status]} dot={false}>{LABEL[row.status]}</Badge>,
    },
    {
      key: 'lastReplyAt',
      label: 'Last activity',
      render: row => <span className="text-sm text-muted">{dateTimeStr(row.lastReplyAt || row.createdAt)}</span>,
    },
    {
      key: 'actions',
      label: '',
      align: 'right',
      render: row => <Button size="sm" onClick={() => setOpen(row)}>Open</Button>,
    },
  ];

  return (
    <>
      <PageHead title="Support requests" subtitle="Help tickets raised from the app." />

      <div className="filters">
        {TABS.map(tab => (
          <Button
            key={tab.value || 'all'}
            size="sm"
            variant={list.filters.status === tab.value ? 'primary' : 'ghost'}
            onClick={() => list.setFilter('status', tab.value)}
          >
            {tab.label}
          </Button>
        ))}
        <SearchBox
          value={list.filters.search}
          onChange={v => list.setFilter('search', v)}
          placeholder="Search title or number…"
        />
        <div className="grow" />
        <span className="text-muted text-sm">{list.meta ? `${num(list.meta.total)} requests` : ''}</span>
      </div>

      <DataTable columns={columns} rows={list.rows} loading={list.loading} meta={list.meta} onPage={list.setPage} />

      <Modal
        open={!!open}
        size="lg"
        title={open?.ticketNumber}
        subtitle={open?.title}
        onClose={() => { setOpen(null); setReply(''); setNote(''); }}
        footer={
          <>
            <Button variant="ghost" onClick={() => { setOpen(null); setReply(''); }}>Close</Button>
            <Button variant="soft" loading={busy} onClick={sendReply}>Send reply</Button>
            {open?.status !== 'resolved' && (
              <Button loading={busy} onClick={resolve}>Mark resolved</Button>
            )}
          </>
        }
      >
        {open && (
          <>
            <KV
              items={[
                { label: 'Raised by', value: open.raisedBy?.name },
                { label: 'Company', value: open.company?.name },
                { label: 'Contact', value: open.raisedBy?.mobile || open.raisedBy?.email },
                { label: 'Raised', value: dateTimeStr(open.createdAt) },
                { label: 'Status', value: LABEL[open.status] },
              ]}
            />

            <div className="ticket-body">{open.description}</div>

            {(open.attachments || []).length > 0 && (
              <div className="ticket-files">
                {open.attachments.map(url => (
                  <a key={url} href={mediaUrl(url)} target="_blank" rel="noreferrer">
                    <img src={mediaUrl(url)} alt="Attachment" />
                  </a>
                ))}
              </div>
            )}

            <div className="ticket-thread">
              {(open.messages || []).map(message => (
                <div
                  key={message._id}
                  className={`ticket-msg ${message.byRole === 'superadmin' ? 'ours' : 'theirs'}`}
                >
                  <div className="who">
                    {message.byRole === 'superadmin' ? 'Support' : message.by?.name || 'User'}
                    <span className="when">{dateTimeStr(message.at)}</span>
                  </div>
                  <div>{message.body}</div>
                </div>
              ))}
              {!(open.messages || []).length && (
                <div className="text-muted text-sm">No replies yet.</div>
              )}
            </div>

            <Textarea
              label="Reply"
              value={reply}
              onChange={e => setReply(e.target.value)}
              rows={3}
              placeholder="Write back to the customer…"
            />

            {open.status !== 'resolved' && (
              <Textarea
                label="Resolution note (optional)"
                value={note}
                onChange={e => setNote(e.target.value)}
                rows={2}
                placeholder="Shown to the customer when you mark this resolved."
              />
            )}
          </>
        )}
      </Modal>
    </>
  );
};

export default Tickets;
